import { useCallback, useEffect, useState } from "react";
import { fetchModulePage, fetchModulePageV2 } from "../api/homePageApi";
import normalizeRecord from "../api/normalizeRecord";

/**
 * Shared, module-agnostic hook for the Home Page Data API. Every module tab
 * uses this same hook — only `moduleKey` differs per call site, same pattern
 * as useModuleSearch.
 *
 * The endpoint takes one module per call and returns a flat response
 * ({ records, totalCount, failedCount, page, limit, hasNextPage }), so each
 * tab fetches only its own module and switching tabs never re-computes the
 * others. Records are normalized through normalizeRecord so downstream
 * components never see per-module field-name differences.
 *
 * const { records, page, ... } = useHomeModuleList({ moduleKey: 'discovery', userId, customerId });
 */
// apiVersion 2 = merged Brand Assessment + Category list (getHomePageDataV2); default 1 = unchanged.
export default function useHomeModuleList({ moduleKey, userId, customerId, limit = 5, apiVersion = 1, includeCategory = true }) {
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyModuleData = useCallback((data) => {
    if (!data) return;
    setRecords((data.records || []).map(normalizeRecord).filter(Boolean));
    setPage(data.page || 1);
    setTotalCount(data.totalCount || 0);
    setFailedCount(data.failedCount || 0);
    setHasNextPage(!!data.hasNextPage);
  }, []);

  const goToPage = useCallback(
    async (nextPage) => {
      if (!moduleKey || !userId || !customerId || nextPage < 1) return;
      setLoading(true);
      setError(null);
      try {
        applyModuleData(
          apiVersion === 2
            ? await fetchModulePageV2({ userId, customerId, page: nextPage, limit, includeCategory })
            : await fetchModulePage({ userId, customerId, moduleKey, page: nextPage, limit })
        );
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [moduleKey, userId, customerId, limit, apiVersion, includeCategory, applyModuleData]
  );

  const loadFirstPage = useCallback(() => goToPage(1), [goToPage]);

  useEffect(() => {
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey, userId, customerId, apiVersion, includeCategory]);

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  return {
    records,
    page,
    limit,
    totalPages,
    totalCount,
    failedCount,
    hasNextPage,
    loading,
    error,
    goToPage,
    refresh: loadFirstPage,
  };
}
